import type { CreateWorkspaceInput } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  createWorkspace,
  listOwnGrants,
  listWorkspaces,
  renameWorkspace,
  setWorkspaceArchived,
} from './api';

// Under the `org-scope` prefix on purpose: `orgKeys.scope` invalidates these too (same string, so the
// two entity slices stay independent).
export const workspaceKeys = {
  list: ['org-scope', 'workspaces'] as const,
  ownGrants: (userId: string) => ['org-scope', 'grants', userId] as const,
};

export function useWorkspaces() {
  return useQuery({ queryKey: workspaceKeys.list, queryFn: () => listWorkspaces(supabase) });
}

export function useOwnGrants(userId: string) {
  return useQuery({
    queryKey: workspaceKeys.ownGrants(userId),
    queryFn: () => listOwnGrants(supabase, userId),
    enabled: userId !== '',
  });
}

export function useCreateWorkspace(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) => createWorkspace(supabase, organizationId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-scope'] }),
  });
}

export function useRenameWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => renameWorkspace(supabase, workspaceId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-scope'] }),
  });
}

export function useSetWorkspaceArchived(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archived: boolean) => setWorkspaceArchived(supabase, workspaceId, archived),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-scope'] }),
  });
}
